import { parseToDateString, getDateOfWeek } from '../date-picker/index.js';

/**
 * Lấy tất cả các ngày cụ thể trong tháng mà được enable dựa trên availability
 * @param {Object} availability - Availability theo ngày trong tuần { Mon: {...}, Tue: {...}, ... }
 * @param {number} month - Tháng (0-11)
 * @param {number} year - Năm
 * @returns {Array<Date>} Mảng các Date được enable
 */
export function getEnabledDatesInMonth(availability, month, year) {
	const enabledDates = [];
	const daysInMonth = new Date(year, month + 1, 0).getDate();

	for (let day = 1; day <= daysInMonth; day++) {
		const date = new Date(year, month, day);
		const dayOfWeek = getDateOfWeek(date); // 'Mon', 'Tue', ...
		// Check if this day of week is enabled in availability
		if (availability[dayOfWeek]?.enabled) {
			enabledDates.push(date);
		}
	}

	return enabledDates;
}


/**
 * Parse time string (12h or 24h format) to minutes from midnight
 * @param {string} timeStr - Time string like "9:00 am", "17:00", "09:00:00"
 * @returns {number} Minutes from midnight
 */
export function timeToMinutes(timeStr) {
	if (!timeStr) return 0;

	// Handle 24h format with seconds (09:00:00)
	const match24hWithSeconds = timeStr.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
	if (match24hWithSeconds) {
		const hours = parseInt(match24hWithSeconds[1], 10);
		const minutes = parseInt(match24hWithSeconds[2], 10);
		return hours * 60 + minutes;
	}

	// Handle 12h format (9:00 am, 5:00 pm) or 24h without seconds (09:00)
	const match12h = timeStr.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
	if (match12h) {
		let hours = parseInt(match12h[1], 10);
		const minutes = parseInt(match12h[2], 10);
		const period = match12h[3]?.toLowerCase();

		if (period === 'pm' && hours !== 12) hours += 12;
		if (period === 'am' && hours === 12) hours = 0;

		return hours * 60 + minutes;
	}

	return 0;
}

export function minutesToTime(minutes) {
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;
	return `${hours < 10 ? '0' : ''}${hours}:${remainingMinutes < 10 ? '0' : ''}${remainingMinutes}`;
}

/**
 * Tính tổng phút available cho một ngày
 * @param {Object} dayAvailability - { enabled: true, start_time: '9:00 am', end_time: '5:00 pm' }
 * @returns {number} Tổng phút available
 */
export function getAvailableMinutes(dayAvailability) {
	if (!dayAvailability?.enabled) return 0;
	return timeToMinutes(dayAvailability.end_time) - timeToMinutes(dayAvailability.start_time);
}

/**
 * Tính tổng phút đã booking cho một ngày cụ thể
 * @param {string} dateStr - Ngày cụ thể 'YYYY-MM-DD'
 * @param {Array} bookings - Danh sách bookings
 * @returns {number} Tổng phút đã booking
 */
export function getBookedMinutesForDate(dateStr, bookings) {
	if (!bookings?.length) return 0;

	return bookings
		.filter(b => b.start_at.split(' ')[0] === dateStr)
		.reduce((total, b) => {
			const start = timeToMinutes(b.start_at.split(' ')[1]);
			const end = timeToMinutes(b.end_at.split(' ')[1]);
			return total + (end - start);
		}, 0);

}

/**
 * Check xem một ngày cụ thể có full booking không
 * @param {Date} date - Ngày cần check
 * @param {Object} availability - Availability theo ngày trong tuần
 * @param {Array} bookings - Danh sách bookings
 * @returns {boolean} true nếu full, false nếu còn chỗ
 */
export function isDateFullyBooked(date, availability, bookings) {
	const dayOfWeek = getDateOfWeek(date);
	const dayAvailability = availability[dayOfWeek];

	// Không enabled → coi như full
	if (!dayAvailability?.enabled) return true;

	const dateStr = parseToDateString(date);
	const availableMinutes = getAvailableMinutes(dayAvailability);
	const bookedMinutes = getBookedMinutesForDate(dateStr, bookings);

	return bookedMinutes >= availableMinutes;
}

export function add30Minutes(time) {
	const [h, m] = time.split(':').map(Number);
	const date = new Date();
	date.setHours(h);
	date.setMinutes(m + 30);
	return date.toTimeString().slice(0, 5);
}

export function isSlotBooked(date, startTime, endTime, bookings) {

	const slotStart = new Date(`${parseToDateString(date)}T${startTime}:00`);
	const slotEnd = new Date(`${parseToDateString(date)}T${endTime}:00`);
	for (const booking of bookings) {
		const bookingStart = new Date(booking.start_at);
		const bookingEnd = new Date(booking.end_at);

		// Check for overlap
		if (slotStart < bookingEnd && slotEnd > bookingStart) {
			return true;
		}
	}

	return false;
}

export function isSlotPast(date, endTime) {
	const slotEnd = new Date(`${parseToDateString(date)}T${endTime}:00`);
	const now = new Date();
	return slotEnd < now;
}

/**
 * Calculate duration in minutes between two times
 * @param {string} startTime - Start time (HH:MM)
 * @param {string} endTime - End time (HH:MM)
 * @returns {number} Duration in minutes
 */
export function calculateDuration(startTime, endTime) {
	const [startH, startM] = startTime.split(':').map(Number);
	const [endH, endM] = endTime.split(':').map(Number);

	const startMinutes = startH * 60 + startM;
	const endMinutes = endH * 60 + endM;

	return endMinutes - startMinutes;
}


export function updateBookingSuccessPopup(date, time) {
	// Format date to readable format: "Tuesday, 14 November 2024"
	const dateObj = new Date(date);
	const locale = wpcore.direct_t({ en: 'en-US', fr: 'fr-FR' });
	const formattedDate = dateObj.toLocaleDateString(locale, {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});

	// Update date element in popup
	const dateElement = document.getElementById('booking-success-date');
	if (dateElement) {
		dateElement.textContent = formattedDate;
	}

	// Update time element in popup
	const timeElement = document.getElementById('booking-success-time');
	if (timeElement) {
		// Format time from "09:30" to "09:30 AM"
		const [hours, minutes] = time.split(':');
		const hour = parseInt(hours);
		const period = hour >= 12 ? 'PM' : 'AM';
		const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
		timeElement.textContent = `${displayHour}:${minutes} ${period}`;
	}
}
